import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, MapPin, Maximize2 } from 'lucide-react';

export interface RoutePoint {
  lat: number;
  lng: number;
  kind: 'from' | 'to';
  title: string;
  subtitle?: string;
  lines?: string[];
}

const FROM_COLOR = '#059669'; // emerald-600
const TO_COLOR = '#dc2626'; // red-600

function pinIcon(kind: 'from' | 'to') {
  const color = kind === 'from' ? FROM_COLOR : TO_COLOR;
  const html = `<svg width="26" height="36" viewBox="0 0 26 36" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C5.8 0 0 5.8 0 13c0 9 13 23 13 23s13-14 13-23C26 5.8 20.2 0 13 0z" fill="${color}"/>
      <circle cx="13" cy="13" r="5" fill="#ffffff"/></svg>`;
  return L.divIcon({ html, className: 'route-pin', iconSize: [26, 36], iconAnchor: [13, 36], popupAnchor: [0, -34] });
}

/** Fit the map to show every point (or center on the single one). */
function FitBounds({ points }: { points: RoutePoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 11);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 12 });
    }
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map, points]);
  return null;
}

/** Raw interactive route map (OpenStreetMap tiles, no API key). */
export function RouteMap({
  points,
  height = 360,
  interactive = true,
}: {
  points: RoutePoint[];
  height?: number | string;
  interactive?: boolean;
}) {
  const line = points.length >= 2 ? points.map((p) => [p.lat, p.lng] as [number, number]) : [];
  const center: [number, number] = points.length ? [points[0].lat, points[0].lng] : [22.35, 78.66]; // India

  return (
    <MapContainer
      center={center}
      zoom={5}
      // Trap Leaflet's internal panes/controls (z-index 200–1000) inside the map's own
      // stacking context so they can't paint over sibling overlays like the order-detail
      // "Downloads" dropdown. `isolation:isolate` + `position:relative;zIndex:0` keeps the
      // whole map at the base level of the page's stacking order.
      style={{ height, width: '100%', position: 'relative', zIndex: 0, isolation: 'isolate' }}
      scrollWheelZoom={interactive}
      dragging={interactive}
      doubleClickZoom={interactive}
      zoomControl={interactive}
      touchZoom={interactive}
      boxZoom={interactive}
      keyboard={interactive}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; OpenStreetMap contributors'
      />
      {line.length >= 2 && (
        <Polyline positions={line} pathOptions={{ color: '#2563eb', weight: 3, dashArray: '6 7', opacity: 0.85 }} />
      )}
      {points.map((p, i) => (
        <Marker key={i} position={[p.lat, p.lng]} icon={pinIcon(p.kind)}>
          <Popup>
            <div style={{ minWidth: 160 }}>
              <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.4, color: p.kind === 'from' ? FROM_COLOR : TO_COLOR }}>
                {p.kind === 'from' ? 'FROM · ORIGIN' : 'TO · DESTINATION'}
              </div>
              <div style={{ fontWeight: 600, marginTop: 2 }}>{p.title}</div>
              {p.subtitle && <div style={{ color: '#64748b', fontSize: 12 }}>{p.subtitle}</div>}
              {(p.lines ?? []).filter(Boolean).map((l, j) => (
                <div key={j} style={{ fontSize: 12, marginTop: 1 }}>{l}</div>
              ))}
            </div>
          </Popup>
        </Marker>
      ))}
      <FitBounds points={points} />
    </MapContainer>
  );
}

/**
 * A compact map "preview" card (a real, non-interactive mini-map) that opens the full interactive
 * map with from/to markers + popups in a modal on click.
 */
export function RouteMapCard({
  points,
  title = 'Route Map',
  loading,
  unavailableText,
  previewHeight = 150,
}: {
  points: RoutePoint[];
  title?: string;
  loading?: boolean;
  unavailableText?: string;
  previewHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const hasAny = points.length > 0;

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
        <div className="text-sm font-medium text-slate-700 flex items-center gap-1"><MapPin size={14} /> {title}</div>
        {hasAny && (
          <button onClick={() => setOpen(true)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <Maximize2 size={12} /> Open map
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ height: previewHeight }} className="flex items-center justify-center text-xs text-slate-400">Loading map…</div>
      ) : hasAny ? (
        <div className="relative cursor-pointer" onClick={() => setOpen(true)} style={{ height: previewHeight }} title="Click to view the full route map">
          <div className="pointer-events-none absolute inset-0">
            <RouteMap points={points} height={previewHeight} interactive={false} />
          </div>
          <div className="absolute bottom-1 right-1 bg-white/90 text-[10px] px-1.5 py-0.5 rounded shadow">Click to enlarge</div>
        </div>
      ) : (
        <div style={{ height: previewHeight }} className="flex items-center justify-center text-xs text-slate-400 px-3 text-center">
          {unavailableText ?? 'Map not available for these locations'}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-[900px] max-w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50">
              <div className="font-semibold text-slate-800 flex items-center gap-1"><MapPin size={16} /> {title}</div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-800"><X size={18} /></button>
            </div>
            <div className="p-3">
              <RouteMap points={points} height={520} interactive />
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                {points.map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span style={{ background: p.kind === 'from' ? FROM_COLOR : TO_COLOR }} className="inline-block w-2.5 h-2.5 rounded-full" />
                    <b>{p.kind === 'from' ? 'From' : 'To'}:</b> {p.title}{p.subtitle ? ` — ${p.subtitle}` : ''}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
